import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Divider, Form, Input, message, Typography } from 'antd'

import {
    uploadResume,
    uploadResumeS3Url,
} from '../../candidate/service/candidate.service'
import { setCandidateResumeUrl } from '../../candidate/store/candidate.slice'

import EducationSection from './sections/education/educationSection'
import ExperienceSection from './sections/experience/experienceSection'
import OverviewSection from './sections/overview/overviewSection'

import {
    setResumeDraft,
    setProfileImageUrl,
    AddCandidatePublic,
} from './store/applicationDraft.slice'
import { useParams } from 'react-router-dom'
import ImportResume from './sections/importResume'
import SuccessFormPage from './successFormPage'

const { TextArea } = Input

const ApplicationForm = () => {
    const { Title } = Typography

    const dispatch = useDispatch()
    const [form] = Form.useForm()
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [submitLoader, setSubmitLoader] = useState(false)

    const { candidateId, cnadidateResusmeUrl, resumeFile } = useSelector(
        (state) => state.candidateSlice
    )
    const draft = useSelector((state) => state.applicationDraftSlice)

    const { id } = useParams()

    const { overview: currentData } = useSelector(
        (state) => state.applicationDraftSlice
    )

    useEffect(() => {
        if (!currentData || !form) return

        // Build phone number safely
        let phone = ''
        if (currentData?.primaryContactNumber) {
            if (currentData?.primaryContactNumberCountryCode) {
                phone =
                    currentData.primaryContactNumberCountryCode +
                    currentData.primaryContactNumber
            } else {
                // fallback: default India code
                phone = `+91${currentData.primaryContactNumber}`
            }
        }

        form.setFieldsValue({
            name: currentData?.fullName || '',
            email: currentData?.personalEmail || '',
            phone,
            primaryContactNumberCountryCode:
                currentData?.primaryContactNumberCountryCode || '+91',
            primaryContactNumberCountryFlag:
                currentData?.primaryContactNumberCountryFlag || 'IN',
            birthDate: currentData?.dateOfBirth
                ? dayjs(currentData.dateOfBirth)
                : null,
            gender: currentData?.gender || '',
            maritalStatus: currentData?.maritalStatus || '',
            address: currentData?.address || null,
            country: currentData?.country || '',
            state: currentData?.state || '',
            city: currentData?.city || '',
            summary: currentData?.summary || '',
            totalExperience: currentData?.totalExperience || '',
            designation: currentData?.designation
                ? {
                      label: currentData.designation.name,
                      value: currentData.designation._id,
                  }
                : null,
        })
    }, [currentData, form])

    useEffect(() => {
        if (currentData?.resumeUrl) {
            dispatch(setCandidateResumeUrl(currentData?.resumeUrl))

            dispatch(
                setResumeDraft({
                    url: currentData?.resumeUrl,
                    fileType: currentData?.resumeFileType ?? null,
                    fileName: currentData?.resumeFileName ?? null,
                })
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentData?.resumeUrl, dispatch])

    const uploadFileToS3 = async (file) => {
        const payload = {
            data: [{ filename: file.name, contentType: file.type }],
        }
        const { data } = await uploadResume(payload)
        const meta = data?.data?.[0]
        if (!data?.success || !meta) return null
        const res = await uploadResumeS3Url(
            file,
            meta.uploadUrl,
            meta.publicLink,
            meta.name
        )
        if (!res.ok) {
            console.error('Upload failed:', await res.text())
            return null
        }
        return {
            publicLink: meta.publicLink,
            contentType: file.type,
            fileName: file.name,
        }
    }

    const extractValue = (input) => {
        if (Array.isArray(input)) return input.map((i) => i?.value || i)
        if (input && typeof input === 'object') return input.value || input
        return input
    }

    // const handleValuesChange = (_, allValues) => {
    //     form.setFieldValue('name', allValues?.name)

    //     dispatch(
    //         setOverviewDraft({
    //             name: allValues.name,
    //             email: allValues.email,
    //             phone: allValues.phone,
    //             address: allValues.address,
    //             country: allValues.country,
    //             state: allValues.state,
    //             city: allValues.city,
    //             birthDate: allValues.birthDate,
    //             gender: allValues.gender,
    //             maritalStatus: allValues.maritalStatus,
    //             totalExperience: allValues.totalExperience,
    //             designation: allValues.designation,
    //             summary: allValues.summary,
    //         })
    //     )
    // }

    // utility: remove keys where value is null, undefined, "", or empty array
    const removeEmpty = (obj) => {
        return Object.fromEntries(
            Object.entries(obj).filter(([_, v]) => {
                if (v === null || v === undefined) return false
                if (typeof v === 'string' && v.trim() === '') return false
                if (Array.isArray(v) && v.length === 0) return false
                return true
            })
        )
    }

    const transformEducation = (educationArray) => {
        if (!Array.isArray(educationArray)) return []

        return educationArray.map((edu) =>
            removeEmpty({
                institute: edu?.institute?.value || '',
                degree: edu?.degree?.value || '',
                fieldOfStudy: edu?.fieldOfStudy?.value || '',
                skills: (edu?.skills || []).map((s) => s?.value || ''),
                tools: (edu?.tools || []).map((t) => t?.value || ''),
                start: edu?.startDate,
                end: edu?.endDate,
                currentStudy: edu?.currentStudy,
                location: edu?.location,
            })
        )
    }

    const transformExperience = (experienceArray) => {
        if (!Array.isArray(experienceArray)) return []

        return experienceArray.map((exp) =>
            removeEmpty({
                title: exp?.title,
                summary: exp?.summary,
                employmentType: exp?.employmentType?.value || '',
                company: exp?.company?.value || '',
                skills: (exp?.skills || []).map((s) => s?.value || ''),
                tools: (exp?.tools || []).map((t) => t?.value || ''),
                start: exp?.start,
                end: exp?.end,
                currentlyWorking: exp?.currentlyWorking,
                location: exp?.location,
            })
        )
    }

    const onFinish = async () => {
        try {
            const values = await form.validateFields()

            let profileImageUrl = draft.profileImageUrl || null
            if (draft.profileImageFile) {
                const up = await uploadFileToS3(draft.profileImageFile)
                if (!up) throw new Error('Profile image upload failed.')
                profileImageUrl = up.publicLink
                dispatch(setProfileImageUrl(up.publicLink))
            }

            setSubmitLoader(true)

            let resumeLink =
                cnadidateResusmeUrl || draft.resumeDraft?.url || null
            let resumeType =
                resumeFile?.type || draft.resumeDraft?.fileType || null
            let resumeName =
                resumeFile?.name || draft.resumeDraft?.fileName || null

            let phoneNumber = values?.phone || ''
            if (
                phoneNumber.startsWith(values?.primaryContactNumberCountryCode)
            ) {
                phoneNumber = phoneNumber.replace(
                    values.primaryContactNumberCountryCode,
                    ''
                )
            }

            const payloadBase = {
                fullName: values.name,
                personalEmail: values.email,
                primaryContactNumber: phoneNumber,
                dateOfBirth: values.birthDate
                    ? values.birthDate.valueOf()
                    : null,
                gender: values.gender,
                maritalStatus: values.maritalStatus,
                address: values.address,
                country: values.country,
                state: values.state,
                city: values.city,
                summary: values.summary,
                profilePicture: profileImageUrl,
                totalExperience: Number(values.totalExperience),
                designation: extractValue(values?.designation),
                primaryContactNumberCountryCode:
                    values?.primaryContactNumberCountryCode,
                primaryContactNumberCountryFlag:
                    values?.primaryContactNumberCountryFlag,
                candidateId,
                isAddedByAI: false,
                jobId: id,
                education: transformEducation(draft.education),
                experience: transformExperience(draft.experience),
                status: 'applied',
            }

            const payload =
                candidateId === null
                    ? {
                          ...payloadBase,
                          resumeUrl: resumeLink,
                          fileType: resumeType,
                          fileName: resumeName,
                      }
                    : payloadBase

            const data = await dispatch(AddCandidatePublic(payload))

            if (data?.payload?.data?.success) {
                setSubmitLoader(false)
                setIsSubmitted(true)
            } else {
                setSubmitLoader(false)
            }
        } catch (err) {
            setSubmitLoader(false)
            setIsSubmitted(false)
            message.error(err?.response?.data?.message || 'Submit failed.')
        }
    }

    return isSubmitted ? (
        <SuccessFormPage currentData={currentData} />
    ) : (
        <Form
            layout="vertical"
            form={form}
            // onValuesChange={handleValuesChange}
            onFinish={onFinish}
            style={{ width: '100%' }}
            className="candidate-public-page"
        >
            <div
                style={{
                    marginBottom: '20px',
                }}
            >
                <ImportResume />
                <OverviewSection currentData={currentData} form={form} />
            </div>
            <Title level={4}>Profile</Title>
            <Divider
                style={{
                    marginTop: '10px',
                    marginBottom: '10px',
                }}
            />
            <div
                style={{
                    margin: '20px 0px 10px 0px',
                }}
            >
                <EducationSection />
            </div>
            <div
                style={{
                    margin: '30px 0px',
                }}
            >
                <ExperienceSection />
            </div>
            <Form.Item
                label="Summary"
                name="summary"
                style={{ marginTop: '10px' }}
                rules={[{ required: true, message: 'Summary is required' }]}
            >
                <TextArea rows={5} placeholder="Summary" />
            </Form.Item>
            <Form.Item style={{ marginTop: '15px' }}>
                <Button
                    htmlType="submit"
                    style={{
                        height: '40px',
                        width: '100%',
                        padding: '0px 24px',
                    }}
                    loading={submitLoader}
                >
                    Submit Application
                </Button>
            </Form.Item>
        </Form>
    )
}

export default ApplicationForm
